import { lazy } from 'react'
import { Sentry } from '../analytics/sentry'

/**
 * Returns true when the error looks like a failed dynamic import caused by a
 * missing / unreachable chunk — the canonical symptom of a stale deployment
 * where content-hashed filenames have changed on the CDN.
 */
export function isChunkLoadError(error) {
  if (!error) return false
  const msg = (error.message || '').toLowerCase()
  return (
    // Chromium
    msg.includes('failed to fetch dynamically imported module') ||
    // Safari
    msg.includes('importing a module script failed') ||
    // Firefox
    msg.includes('error loading dynamically imported module') ||
    // Webpack-style (unlikely with Vite, but defensive)
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    // Vite CSS preload failures
    msg.includes('unable to preload css') ||
    // Stale deploy where the chunk URL no longer exists: the request 404s
    // through the SPA fallback and Netlify returns index.html with a 200, so
    // the browser parses HTML as a module and throws a SyntaxError on the
    // leading '<' of <!doctype html>. This happens on long-lived tabs running
    // an old build whose content-hashed chunks were deleted by later deploys.
    // isChunkLoadError is only ever consulted inside the dynamic-import catch,
    // so a SyntaxError here means HTML-served-as-JS, not a real code bug.
    (error.name === 'SyntaxError' &&
      (msg.includes("unexpected token '<'") ||
        // Firefox phrasing for the same HTML-as-module parse failure
        msg.includes('expected expression, got'))) ||
    error.name === 'ChunkLoadError'
  )
}

/**
 * Force a full page reload that bypasses the service worker and browser caches.
 *
 * When a new deployment replaces content-hashed chunks, a stale service worker
 * (registerType: 'prompt', user hasn't accepted the update) may keep serving
 * the old index.html — which still references the now-deleted chunk URLs.
 * Unregistering all service workers and clearing their caches before reloading
 * ensures the browser fetches the new HTML from the network.
 */
async function hardReload() {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((r) => r.unregister()))
    }
  } catch { /* best-effort */ }
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch { /* best-effort */ }
  window.location.reload()
}

/**
 * Wraps React.lazy with retry logic to handle failed chunk loads after deployments.
 * When Vite builds new chunks with different hashes, users with cached HTML may try
 * to load old chunks that no longer exist. This wrapper:
 * 1. Detects chunk-specific load failures (vs. other import errors)
 * 2. Retries the import once (handles transient network errors)
 * 3. If retry fails, clears service-worker caches and hard-reloads the page to
 *    fetch fresh HTML with correct chunk references
 * 4. Uses per-path sessionStorage flags with a 30-second TTL to prevent infinite
 *    reload loops while still allowing recovery on different routes
 * 5. Reports failures to Sentry — including before reload, via flush() — so we can
 *    detect when stale-deploy chunk failures are happening in production
 */
export default function lazyWithRetry(componentImport) {
  return lazy(() =>
    componentImport().catch((error) => {
      // Only intercept chunk load failures; let other import errors propagate
      if (!isChunkLoadError(error)) throw error

      // Retry the import once (handles transient network blips)
      return componentImport().catch(async (retryError) => {
        // Use a path-specific key so different routes can independently reload
        const storageKey = `chunk_reload:${window.location.pathname}`
        const lastReload = sessionStorage.getItem(storageKey)

        // Allow a reload if we haven't tried in the last 30 seconds
        // (prevents infinite loops while allowing recovery after navigation)
        const canReload =
          !lastReload || Date.now() - Number(lastReload) > 30_000

        if (canReload) {
          sessionStorage.setItem(storageKey, String(Date.now()))
          Sentry.captureException(error, {
            tags: { failure_type: 'chunk_load', recovery: 'reloading' },
          })
          // Flush in-flight events before navigation kills them
          try {
            await Sentry.flush(2000)
          } catch { /* swallow */ }
          await hardReload()
          // Return a component that renders nothing while the page reloads
          return { default: () => null }
        }

        // Already reloaded recently — clear flag and surface the original error
        // so ErrorBoundary can show a user-friendly "please refresh" message
        sessionStorage.removeItem(storageKey)
        Sentry.captureException(retryError, {
          tags: { failure_type: 'chunk_load', recovery: 'failed_after_reload' },
        })
        throw retryError
      })
    })
  )
}
